import {connect} from 'react-redux';

import {AsyncHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkModel,
} from '../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {getCalendarWeekStartDay} from '../../../../store/data/preferences/preferencesSelectors';
import {
  createSchedulingCalendarsPanelTransitionAction,
  saveSchedulingLinkAsyncAction,
  updateDraftSchedulingLinkAction,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingActions';
import {
  getDraftSchedulingLink,
  getSchedulingPanel,
} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingSelectors';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {inLegacyTransientPanel} from '../../../legacyTransientPanel';
import {InstantReschedulingLinkDialog} from './instantReschedulingLinkDialog';

/*
 * Props.
 */

interface InstantReschedulingLinkDialogContainerProps {
  panelId: CalendarsPanelId;
  parentPanelId: CalendarsPanelId;
  schedulingLinkId: number;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InstantReschedulingLinkDialogContainerProps>()(() => ({
  draftLink: getDraftSchedulingLink,
  schedulingPanel: getSchedulingPanel,
}));

const mapDispatchToProps = makeMapDispatchToProps<InstantReschedulingLinkDialogContainerProps>()(
  ({panelId}) => ({
    onUpdateSchedulingLink: (changes) => updateDraftSchedulingLinkAction(panelId, changes),
    saveSchedulingLinkAsync: (draftLink) => saveSchedulingLinkAsyncAction(panelId, draftLink),
    weekStartDay: getCalendarWeekStartDay,
  }),
);

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: InstantReschedulingLinkDialogContainerProps,
) {
  const {draftLink} = stateProps;
  const {saveSchedulingLinkAsync, onUpdateSchedulingLink} = dispatchProps;

  const onUpdateAdvanceNoticeMinutes = (advanceNoticeMinutes: number) =>
    onUpdateSchedulingLink({advanceNoticeMinutes});
  const onUpdateMeetingBufferMinutes = (meetingBufferMinutes: number) =>
    onUpdateSchedulingLink({meetingBufferMinutes});

  const {advanceNoticeMinutes, meetingBufferMinutes} = draftLink;

  const onSaveClick: AsyncHandler<SchedulingLinkModel> = async () => await saveSchedulingLinkAsync(draftLink);

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    advanceNoticeMinutes,
    meetingBufferMinutes,
    onSaveClick,
    onUpdateMeetingBufferMinutes,
    onUpdateAdvanceNoticeMinutes,
  };
}

/*
 * Panel.
 */

export const InstantReschedulingLinkDialogContainer = inLegacyTransientPanel(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(InstantReschedulingLinkDialog),
  ({panelId, parentPanelId, schedulingLinkId}) =>
    createSchedulingCalendarsPanelTransitionAction(panelId, parentPanelId, schedulingLinkId, {
      availability: {
        type: SchedulingLinkAvailabilityTypesEnum.REPEATING,
        blocks: [],
      },
    }),
);
